import type { Action, RunnableAction, WebhookAction } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { Button, Card, CardHeader, Field, Icon, Input, Switch, Textarea } from "../../ui/index.js";
import { ActionForm, type ActionFormSubmit, DESCRIPTION_HINT } from "./ActionForm.js";
import { SECRET_MIN_LENGTH, draftFromAction, specOf } from "./draft.js";
import { TestDrawer } from "./TestDrawer.js";

/** Edits or deletes one action. `onDone` runs after a save, a cancel, or a delete. */
export function ActionEditor({ action, onDone }: { action: Action; onDone: () => void }) {
  return action.type === "hold" || action.type === "ignore" ? <SingletonForm key={action.id} action={action} onDone={onDone} /> : <RunnableEditor key={action.id} action={action} onDone={onDone} />;
}

function useSaveAction(): { busy: boolean; save: (action: Action, successMessage: string) => Promise<boolean> } {
  const api = useApi();
  const { actions, setActions } = useTenant();
  const task = useTask();
  async function save(action: Action, successMessage: string): Promise<boolean> {
    return task.run(async () => {
      const saved = await api.call("actionReplace", { params: { id: action.id }, body: specOf(action) });
      setActions(actions.map((existing) => (existing.id === saved.id ? saved : existing)));
    }, successMessage);
  }
  return { busy: task.busy, save };
}

function SingletonForm({ action, onDone }: { action: Extract<Action, { type: "hold" | "ignore" }>; onDone: () => void }) {
  const { busy, save } = useSaveAction();
  const [description, setDescription] = useState(action.description);
  const [enabled, setEnabled] = useState(action.enabled);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (description.trim() === "") {
      setError("Describe when to use it.");
      return;
    }
    setError(undefined);
    const next: Action = action.type === "hold" ? { ...action, description: description.trim() } : { ...action, description: description.trim(), enabled };
    if (await save(next, "Saved.")) onDone();
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Card>
        <CardHeader eyebrow="Built-in Action" />
        <Field label="Description" hint={DESCRIPTION_HINT} error={error}>
          {({ id, describedBy, invalid }) => (
            <Textarea id={id} rows={3} maxLength={500} value={description} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setDescription(event.target.value); }} />
          )}
        </Field>
        {action.type === "ignore" ? (
          <Switch checked={enabled} onChange={setEnabled} label="Enabled" description="When off, email is never ignored; it is held instead." />
        ) : (
          <p className="muted small">Hold is always on. It is where email goes when nothing else fits.</p>
        )}
        <div className="button-row">
          <Button type="submit" variant="primary" busy={busy}>
            Save
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </Card>
    </form>
  );
}

function RunnableEditor({ action, onDone }: { action: RunnableAction; onDone: () => void }) {
  const { busy, save } = useSaveAction();
  const [testOpen, setTestOpen] = useState(false);

  async function submit(submit: ActionFormSubmit): Promise<void> {
    if (submit.intent !== "edit") return;
    if (submit.spec.type !== action.type) return;
    const next: Action = { ...submit.spec, id: action.id };
    if (await save(next, "Saved.")) onDone();
  }

  return (
    <div className="stack-lg">
      <ActionForm
        initial={draftFromAction(action)}
        intent="edit"
        busy={busy}
        onSubmit={(result) => void submit(result)}
        onCancel={onDone}
        extra={
          action.type === "webhook" ? (
            <Button variant="secondary" onClick={() => { setTestOpen(true); }}>
              <Icon name="send" size={14} /> Test
            </Button>
          ) : undefined
        }
      />
      {action.type === "webhook" ? <RotateSecret action={action} /> : null}
      <DeleteAction action={action} onDone={onDone} />
      {action.type === "webhook" ? <TestDrawer open={testOpen} onClose={() => { setTestOpen(false); }} action={action} /> : null}
    </div>
  );
}

function RotateSecret({ action }: { action: WebhookAction }) {
  const api = useApi();
  const task = useTask();
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (secret.length < SECRET_MIN_LENGTH) {
      setError(`At least ${SECRET_MIN_LENGTH} characters.`);
      return;
    }
    setError(undefined);
    const ok = await task.run(() => api.call("actionRotateSecret", { params: { id: action.id }, body: { secret } }), "Secret replaced. New calls are signed with it.");
    if (ok) setSecret("");
  }

  return (
    <Card>
      <CardHeader eyebrow="Rotate Secret" />
      <form className="inline-form" onSubmit={(event) => void submit(event)} noValidate>
        <Field label="New secret" hint="The old secret stops working at once." error={error}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} className="mono" type="password" autoComplete="new-password" value={secret} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setSecret(event.target.value); }} />
          )}
        </Field>
        <Button type="submit" busy={task.busy}>
          Rotate secret
        </Button>
      </form>
    </Card>
  );
}

function DeleteAction({ action, onDone }: { action: RunnableAction; onDone: () => void }) {
  const api = useApi();
  const { actions, setActions } = useTenant();
  const task = useTask();
  const [confirming, setConfirming] = useState(false);

  async function remove(): Promise<void> {
    const ok = await task.run(async () => {
      await api.call("actionDelete", { params: { id: action.id } });
      setActions(actions.filter((existing) => existing.id !== action.id));
    }, `Deleted "${action.name}".`);
    if (ok) onDone();
  }

  return (
    <Card className="danger-card">
      <CardHeader eyebrow="Delete Action" />
      {confirming ? (
        <div className="button-row">
          <span className="small">Delete "{action.name}"? Email it would have handled goes to the other actions.</span>
          <Button variant="danger" busy={task.busy} onClick={() => void remove()}>
            Yes, delete
          </Button>
          <Button variant="ghost" onClick={() => { setConfirming(false); }}>
            Keep it
          </Button>
        </div>
      ) : (
        <Button variant="danger" onClick={() => { setConfirming(true); }}>
          <Icon name="trash" size={14} /> Delete action
        </Button>
      )}
    </Card>
  );
}
